import React, { useState } from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Card from "components/Card";
import { Row } from "components/Containers";
import { Spinner } from "components/Loader";
import Tags, { TAG_SIZE } from "components/Tags";
import { colors, fontSize, margins } from "style";
import { PROFILE_SECTIONS } from "constants/index";
import ProfileResumeAutoFillHeader from "./ProfileResumeAutoFillHeader";
import ProfileResumeAutoFillDropzone from "./ProfileResumeAutoFillDropzone";
import ProfileResumeAutoFillProcessing from "./ProfileResumeAutoFillProcessing";

/**
 * ProfileResumeAutoFill
 *
 * Allows a candidate to upload a resume and automatically
 * populate their profile from the resume data.
 *
 * @param {Object}   task
 * @param {Function} onUpload
 * @param {Number}   progress
 * @param {Boolean}  creating
 * @param {Boolean}  profileLoading
 * @param {Boolean}  disabled
 * @param {Node}     disabledMessageId
 */
const ProfileResumeAutoFill = ({
  task,
  onUpload,
  progress,
  creating = false,
  profileLoading = false,
  disabled = false,
  disabledMessageId = "Profile.ProfileResumeAutoFill.DisabledMessage",
  ...props
}) => {
  const [showUploader, setShowUploader] = useState(false);

  const hasTask = Boolean(task);
  const showDropzone = !hasTask || showUploader;

  if (profileLoading) {
    return (
      <Card css={styles.container}>
        <Row css={styles.loading}>
          <Spinner size="2.6rem" margin="0" />
        </Row>
      </Card>
    );
  }

  return (
    <Card css={styles.container}>
      <ProfileResumeAutoFillHeader />

      {showDropzone ? (
        <ProfileResumeAutoFillDropzone
          task={task}
          disabled={disabled}
          creating={creating}
          onUpload={onUpload}
          disabledMessageId={disabledMessageId}
          setShowUploader={setShowUploader}
        />
      ) : (
        <ProfileResumeAutoFillProcessing
          task={task}
          progress={progress}
          disabled={disabled}
          disabledMessageId={disabledMessageId}
          setShowUploader={setShowUploader}
          {...props}
        />
      )}

      <Row css={styles.sections}>
        <span css={styles.label}>
          <FormattedMessage id="Profile.ProfileResumeAutoFill.SectionsTitle" />
        </span>
        <Tags data={PROFILE_SECTIONS} size={TAG_SIZE.small} color={colors.white} background={colors.purpleRainDark3} />
      </Row>

      <p css={styles.disclaimer}>
        <FormattedMessage id="Profile.ProfileResumeAutoFill.AIDisclaimer" />
      </p>
    </Card>
  );
};

const styles = {
  container: css`
    container-type: inline-size;
    gap: ${margins.xsmall};
    margin-bottom: ${margins.small};
  `,
  loading: css`
    justify-content: center;
    min-height: 12rem;
  `,
  label: css`
    color: ${colors.purpleRainDark2};
    font-size: ${fontSize.xsmall};
    font-weight: 600;
  `,
  sections: css`
    flex-wrap: wrap;
    gap: ${margins.xxxsmall};
    padding: ${margins.xxsmall} 1.5rem;
    border-radius: 0.8rem;
    background: ${colors.purpleRainLight8};

    @container (max-width: 52rem) {
      align-items: flex-start;
    }
  `,
  disclaimer: css`
    margin: 0;
    color: ${colors.grayAnatomyLight1};
    font-size: ${fontSize.xxsmall};
    line-height: 1.4;
  `,
};

ProfileResumeAutoFill.propTypes = {
  task: PropTypes.shape({
    state: PropTypes.string,
    agentStatus: PropTypes.string,
    resumeUrl: PropTypes.string,
  }),
  onUpload: PropTypes.func.isRequired,
  progress: PropTypes.number,
  creating: PropTypes.bool,
  profileLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledMessageId: PropTypes.string,
};

export default ProfileResumeAutoFill;
